import React, { useState } from "react";
import IntegrationDataMapping from "../shared/IntegrationDataMapping";

// ---------------------------------------------------------------------------
// Guide sub-components
// ---------------------------------------------------------------------------

type GuideSection = "overview" | "agent-panel" | "workflow-data" | "ai-tools";

const GUIDE_SECTIONS: { id: GuideSection; title: string }[] = [
  { id: "overview",       title: "What is field mapping?" },
  { id: "agent-panel",    title: "Agent CRM panel" },
  { id: "workflow-data",  title: "HTTP POST / workflows" },
  { id: "ai-tools",       title: "AI tools (no mapping)" },
];

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-primary-bg border border-cornflower-blue-200 rounded-lg p-3 mt-3 text-xs text-cornflower-blue-800 leading-relaxed">
      {children}
    </div>
  );
}

function TermBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-3 text-xs text-slate-600 leading-relaxed">
      {children}
    </div>
  );
}

function InlineCode({ children }: { children: string }) {
  return (
    <code className="bg-white px-1 py-0.5 rounded border border-slate-200 text-cornflower-blue-800 font-mono text-[11px]">
      {children}
    </code>
  );
}

function GuideOverview() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">What is Field Mapping?</h3>
      <p className="mb-3">
        Field mapping tells the platform <strong>which Toast data to surface in workflow variables</strong> and what to call it. Toast does not have a traditional customer CRM panel — instead, field mapping is primarily used to pass menu, order, and restaurant data into HTTP POST workflow nodes.
      </p>
      <p className="mb-3">
        Think of it as a <em>variable registry</em>: you pick a Toast object type (MenuItem, MenuGroup, Order, RestaurantInfo), choose the fields you care about, and reference them in POST workflow nodes.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-2">Quick decision guide</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Use case</th>
              <th className="px-3 py-2 text-left">Field mapping needed?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="px-3 py-2">Pass order data into an HTTP POST workflow node</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — required</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">AI answers menu questions and checks hours</td>
              <td className="px-3 py-2 text-slate-500 font-medium">No — AI reads raw data</td>
            </tr>
            <tr>
              <td className="px-3 py-2">Display restaurant info in the agent sidebar</td>
              <td className="px-3 py-2 text-success-green-700 font-medium">Yes — required</td>
            </tr>
          </tbody>
        </table>
      </div>

      <TermBox>
        <strong>Scope type</strong> — Toast data is grouped into object types: MenuItem, MenuGroup, Order, RestaurantInfo. Each mapping panel targets one scope type.
      </TermBox>
    </div>
  );
}

function GuideAgentPanel() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">Agent CRM Panel</h3>
      <p className="mb-3">
        Toast does not have a customer CRM panel in the traditional sense. When an agent handles a conversation, restaurant and order data can be surfaced in the sidebar using field mapping — but there is no persistent per-customer record to display.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Field options explained</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Option</th>
              <th className="px-3 py-2 text-left">Effect</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="px-3 py-2 font-medium">Label</td>
              <td className="px-3 py-2">The display name agents see. Rename <InlineCode>hours_of_operation</InlineCode> to "Hours".</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2 font-medium">Show in search</td>
              <td className="px-3 py-2">Agents can search conversations by this field value.</td>
            </tr>
            <tr>
              <td className="px-3 py-2 font-medium">Show when selected</td>
              <td className="px-3 py-2">Field appears in the detail view when the agent drills into a record.</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2 font-medium">Default value</td>
              <td className="px-3 py-2">Fallback text when the Toast field is empty or null.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox>
        <strong>Note:</strong> Toast AI tools work without field mapping — they answer menu questions, check hours, look up orders, and provide allergen information automatically. Toast does not have a customer CRM panel.
      </InfoBox>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Toast object reference</h4>
      <CodeBlock>{`MenuItem:       name, description, price, category, calories, allergens, modifiers
MenuGroup:      name, items_count, description
Order:          id, status, total, items, special_instructions, created_date
RestaurantInfo: name, address, phone, hours_of_operation`}</CodeBlock>
    </div>
  );
}

function GuideWorkflowData() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">HTTP POST / Workflow Data Passing</h3>
      <p className="mb-3">
        In a workflow, an <strong>HTTP POST node</strong> can call an external API and include Toast data in the request body. To pass a Toast field value, you first map it — that gives it a variable name — then reference that variable in the POST node.
      </p>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">How the variable is formed</h4>
      <p className="text-xs text-slate-600 mb-2">
        Once you map a Toast field, it becomes available as a workflow variable. The variable name follows the pattern <InlineCode>{"{toast.scopeType.fieldName}"}</InlineCode>:
      </p>
      <CodeBlock>{`Scope: Order          →  Field: status
Variable: {toast.order.status}

Scope: RestaurantInfo →  Field: hours_of_operation
Variable: {toast.restaurantinfo.hours_of_operation}`}</CodeBlock>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Example: Send order confirmation</h4>
      <CodeBlock>{`// HTTP POST node — request body
{
  "order_id":     "{toast.order.id}",
  "order_status": "{toast.order.status}",
  "order_total":  "{toast.order.total}",
  "restaurant":   "{toast.restaurantinfo.name}",
  "conversation": "{conversation.id}"
}`}</CodeBlock>

      <InfoBox>
        <strong>Field mapping is the bridge.</strong> Without a mapping entry for <InlineCode>order.status</InlineCode>, the variable <InlineCode>{"{toast.order.status}"}</InlineCode> would be empty at runtime. Map first, then reference in the POST node.
      </InfoBox>
    </div>
  );
}

function GuideAiTools() {
  return (
    <div>
      <h3 className="font-semibold text-slate-800 mb-2">AI Tools — No Field Mapping Required</h3>
      <p className="mb-3">
        Toast AI tools work without field mapping — they answer menu questions, check hours, look up orders, and provide allergen information automatically. Toast does not have a customer CRM panel.
      </p>

      <h4 className="font-medium text-slate-700 mt-3 mb-1">How AI tools work</h4>
      <p className="text-xs text-slate-600 mb-2">
        When an AI workflow node runs, it calls the Toast API at runtime and receives the full raw JSON response. The AI model extracts relevant details and composes a natural language response — no schema configuration needed.
      </p>
      <CodeBlock>{`// What the AI receives internally
{
  "menu_item": {
    "name": "Classic Burger",
    "description": "Angus beef, lettuce, tomato, house sauce",
    "price": 14.99,
    "calories": 680,
    "allergens": ["gluten", "dairy"],
    "modifiers": ["Add bacon +$2", "No onions"]
  }
}

// What the AI says to the visitor
"The Classic Burger is $14.99 — Angus beef with lettuce,
 tomato, and house sauce (680 cal). It contains gluten
 and dairy. You can add bacon for $2 or request no onions."`}</CodeBlock>

      <InfoBox>
        <strong>Why no mapping?</strong> Field mapping is a <em>static variable registry</em> — you define once what to pass into workflows. AI tools are <em>dynamic runtime calls</em> — the AI decides which fields matter based on the visitor's question.
      </InfoBox>

      <h4 className="font-medium text-slate-700 mt-4 mb-1">Comparison: AI tool vs mapped field</h4>
      <div className="border border-slate-200 rounded-lg overflow-hidden text-xs mt-2">
        <table className="w-full">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Capability</th>
              <th className="px-3 py-2 text-left">AI Tool</th>
              <th className="px-3 py-2 text-left">Field Mapping</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs">
            <tr>
              <td className="px-3 py-2">Agent sidebar display</td>
              <td className="px-3 py-2 text-slate-400">No</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Visitor-facing AI answers</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
              <td className="px-3 py-2 text-slate-400">No</td>
            </tr>
            <tr>
              <td className="px-3 py-2">HTTP POST variable injection</td>
              <td className="px-3 py-2 text-slate-400">No</td>
              <td className="px-3 py-2 text-success-green-700">Yes</td>
            </tr>
            <tr className="bg-slate-50">
              <td className="px-3 py-2">Requires configuration</td>
              <td className="px-3 py-2 text-slate-500">No (auto-activated)</td>
              <td className="px-3 py-2 text-slate-500">Yes (this panel)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <TermBox>
        <strong>When you need both:</strong> An AI workflow node can answer "is the Classic Burger gluten-free?" without any mapping. But if the same workflow also sends a POST to your order management system, you need field mapping to supply the <InlineCode>{"{toast.order.id}"}</InlineCode> variable that goes into the POST body.
      </TermBox>
    </div>
  );
}

function FieldMappingGuide() {
  const [guideSection, setGuideSection] = useState<GuideSection>("overview");
  return (
    <div className="bg-white rounded-lg border border-slate-200 mb-4">
      <div className="flex">
        {/* Sidebar nav */}
        <nav className="w-48 shrink-0 border-r border-slate-200 p-3" aria-label="Field mapping guide sections">
          <div className="space-y-0.5">
            {GUIDE_SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setGuideSection(s.id)}
                className={`block w-full text-left px-3 py-1.5 rounded text-xs transition-colors ${
                  guideSection === s.id
                    ? "bg-primary-bg text-primary-active font-medium"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1 p-5 text-sm text-slate-700 leading-relaxed overflow-y-auto max-h-[520px]">
          {guideSection === "overview"      && <GuideOverview />}
          {guideSection === "agent-panel"   && <GuideAgentPanel />}
          {guideSection === "workflow-data" && <GuideWorkflowData />}
          {guideSection === "ai-tools"      && <GuideAiTools />}
        </div>
      </div>
    </div>
  );
}

const SCOPE_OPTIONS = ["MenuItem", "MenuGroup", "Order", "RestaurantInfo"];

interface Props {
  teamId: number;
  selectedConfigId: number;
}

export default function ToastDataMapping(props: Props) {
  return (
    <IntegrationDataMapping
      selectedConfigId={props.selectedConfigId}
      teamId={props.teamId}
      description="Configure which Toast fields are available as workflow variables and in the agent sidebar."
      guide={<FieldMappingGuide />}
      guideLabel="Field Mapping Guide ▾"
      fieldKey="ToastField"
      fieldLabel="Toast Field"
      apiPrefix="Toast/Integrations"
      getFieldsPath="GetToastFields"
      scopeOptions={SCOPE_OPTIONS}
    />
  );
}
